// src/templates/family-about.js - Updated About Us page with i18n support and centralized theming
import React from "react"
import { graphql, Link } from "gatsby"
import { useI18next, useTranslation } from "gatsby-plugin-react-i18next"
import SEO from "../components/seo"
import AdoptionHeader from "../components/AdoptionHeader"
import AdoptionFooter from "../components/AdoptionFooter"
import ContactForm from "../components/contact-form"

const FamilyAbout = ({ data, pageContext }) => {
  const { t } = useTranslation()
  const { language } = useI18next()
  const family = data.account

  if (!family) {
    return (
      <div>
        <AdoptionHeader />
        <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4">
          <div className="text-center">
            <h1 className="mb-4 text-4xl font-bold text-gray-800 font-heading-main">
              {t('common.familyNotFound')}
            </h1>
            <p className="text-lg text-gray-600 font-body">
              {t('common.familyNotFoundMessage')}
            </p>
          </div>
        </div>
        <AdoptionFooter />
      </div>
    )
  }
  let flipbookUrl = '#'
  if (family.flipbook?.length) {
    const agencyFlipbook = family.flipbook.find(fb => fb.agency === family.agency?.mongoId)
    if (agencyFlipbook) {
      if (agencyFlipbook.type === 'heyzine' && agencyFlipbook.heyzine_data) {
        flipbookUrl = `${process.env.HEYZINE_URL}${agencyFlipbook.heyzine_data.url}`
      } else if (agencyFlipbook.type === 'local' && agencyFlipbook.flipbook) {
        flipbookUrl = `${process.env.PF_CDN_URL}${agencyFlipbook.flipbook}`
      }
    }
  }

  const familyName = family.name ? family.name.replace(' / ', ' and ') : "Adoptive Family"
  const displayName = familyName.replace(' Family', '').replace('The ', '')
  
  // Translation helpers for family type
  const familyTypeText = family.account_type === 'Couple' ? t('common.we') : t('common.i')
  const familyTypeText2 = family.account_type === 'Couple' ? t('common.our') : t('common.my')
  const familyTypeText3 = family.account_type === 'Couple' ? t('common.us') : t('common.me')
  
  // Get primary contact info
  const agencyData = family.agency || null
  let primaryPhone = process.env.PRIMARY_CONTACT_NUMBER
    ? "+1" + process.env.PRIMARY_CONTACT_NUMBER.replace(/\D/g, "")
    : "+13176527696"
  let callNumber = primaryPhone
  if (agencyData) {
    if (agencyData.bm_contact) {
      if (agencyData.bm_contact.phone) {
        callNumber = `+1${agencyData.bm_contact.phone.replace(/\D/g, "")}`
      }
    } else if (agencyData.phone?.length && agencyData.phone[0]?.number) {
      primaryPhone =
        (agencyData.phone[0].country_code
          ? agencyData.phone[0].country_code
          : "+1") + agencyData.phone[0].number.replace(/\D/g, "")
      callNumber = primaryPhone
    }
  }
  const formattedPhone = (number) => {
    const cleaned = ('' + number).replace(/\D/g, '')
    const match = cleaned.match(/^1?(\d{3})(\d{3})(\d{4})$/)
    if (match) {
      return `(${match[1]}) ${match[2]}-${match[3]}`
    }
    return number
  }

  const getFamilyDefaultDescription = () => {
    if (!family || !family.default_letter || !family.default_letter.description) {
      return null
    }
    if (language === 'en' || !family.default_letter.translations || !Array.isArray(family.default_letter.translations) || !family.default_letter.translations.length) {
      return family.default_letter.description
    }
    const translated = family.default_letter.translations.find(t => t.lan === language)
    return translated?.description || family.default_letter?.description || null
  }

  // Generate comprehensive SEO data
  const pageTitle = t('about.seoTitle', { familyName, familyTypeText2 })
  const pageDescription = t('about.seoDescription', { familyName, familyTypeText2: familyTypeText2.toLowerCase() })
  
  const keywords = [
    t('about.seoKeywords.aboutUs'),
    `${familyName}`,
    t('about.seoKeywords.adoptionJourney'),
    t('about.seoKeywords.adoptiveParentsStory'),
    t('about.seoKeywords.familyValues'),
    family.contactInfo?.state?.name && t('about.seoKeywords.stateAdoptiveFamily', { state: family.contactInfo.state.name }),
    t('about.seoKeywords.faithAndAdoption'),
    t('about.seoKeywords.hopefulParents')
  ].filter(Boolean)

  // Get the main family photo
  const familyPhoto = family.avatar 
    ? `${process.env.S3_BUCKET_URL}${family.avatar.replace('/thumb/', '/original/')}`
    : ''

  // Helper functions for video handling
  const extractYouTubeId = (url) => {
    if (!url) return null
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([^&\n?#]+)/,
      /^([a-zA-Z0-9_-]{11})$/
    ]
    for (const pattern of patterns) {
      const match = url.match(pattern)
      if (match && match[1]) return match[1]
    }
    return null
  }

  const extractVimeoId = (url) => {
    if (!url) return null
    const patterns = [
      /vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|video\/|)(\d+)/,
      /player\.vimeo\.com\/video\/(\d+)/,
      /^\d+$/
    ]
    for (const pattern of patterns) {
      const match = url.match(pattern)
      if (match && match[1]) return match[1]
    }
    if (/^\d+$/.test(url)) return url
    return null
  }

  const getVideoEmbedUrl = (videoUrl, type) => {
    if (type === 'Youtube') {
      const videoId = extractYouTubeId(videoUrl)
      return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0` : videoUrl
    } else if (type === 'Vimeo') {
      const videoId = extractVimeoId(videoUrl)
      return videoId ? `https://player.vimeo.com/video/${videoId}?autoplay=1&title=0&byline=0&portrait=0` : videoUrl
    }
    return videoUrl
  }

  // Get primary video
  const primaryVideo = family.default_video 
    ? family.videos?.find(v => v._id === family.default_video) || family.videos?.[0]
    : family.videos?.[0]

  return (
    <div className="font-sans bg-gray-50" style={{ margin: 0, padding: 0 }}>
      <SEO
        title={pageTitle}
        description={pageDescription}
        pathname="/about"
        keywords={keywords}
        familyData={family}
        image={familyPhoto}
      />
      
      <AdoptionHeader family={family} agencyId={family.agency?.mongoId} accountId={family.mongoId} />

      <main>
        {/* Hero Section - About Text and Image */}
        <div className="py-16 lg:py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-8 items-center">
              
              {/* Left Side - Family Photo */}
              <div className="order-2 lg:order-1">
                <div className="relative max-w-lg mx-auto">
                  <div className="aspect-[4/3] overflow-hidden rounded-3xl shadow-xl">
                    <img
                      src={familyPhoto}
                      alt={familyName}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>

              {/* Right Side - Content */}
              <div className="order-1 lg:order-2">
                <h2 className="text-2xl lg:text-3xl text-gray-600 mb-4 font-heading-sub">
                  {t('about.greeting', { familyTypeText: family.account_type === 'Couple' ? t('about.weAre') : t('about.iAm') })}
                </h2>
                <h1 className="text-4xl lg:text-5xl xl:text-6xl mb-8 font-heading-main">
                  {displayName}
                </h1>

                {/* About Text */}
                <div className="space-y-4 mb-8">
                {(() => {
                    const content = getFamilyDefaultDescription() || t('about.defaultDescription');
                    
                    // Remove HTML tags for length calculation
                    const textContent = content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
                    const shouldTruncate = textContent.length > 700
                    const truncatedContent = shouldTruncate 
                      ? content.substring(0, 700).replace(/<[^>]*>$/, '') + '...'
                      : content
                    
                    return (
                      <div 
                        className="text-gray-600 leading-relaxed text-base lg:text-lg font-body"
                        dangerouslySetInnerHTML={{ __html: truncatedContent }}
                      />
                    )
                  })()}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    href={flipbookUrl}
                    target="_blank"
                    className="btn-primary text-center"
                    aria-label={t('about.viewProfileBook')}
                  >
                    {t('about.viewProfileBookButton', { familyTypeText2: familyTypeText2.toUpperCase() })}
                  </Link>
                  <Link 
                    to="/contact"
                    className="btn-primary text-center"
                  >
                    {t('about.contactButton', { familyTypeText3: familyTypeText3.toUpperCase() })}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Video Section */}
        {primaryVideo && (
          <div className="py-16 lg:py-24 bg-white">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid lg:grid-cols-2 gap-8 lg:gap-8 items-center">
                
                {/* Left Side - Video Player */}
                <div className="relative">
                  <div className="aspect-video overflow-hidden rounded-3xl shadow-xl bg-black">
                    <iframe
                      src={getVideoEmbedUrl(primaryVideo.url, primaryVideo.type).replace('autoplay=1', 'autoplay=0')}
                      className="w-full h-full"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title={primaryVideo.name || t('about.videoTitle', { familyName })}
                    />
                  </div>
                </div>

                {/* Right Side - Content */}
                <div>
                  <h2 className="text-2xl lg:text-3xl text-gray-600 mb-4 font-heading-sub">
                    {t('about.watchVideo', { familyTypeText2: familyTypeText2.toLowerCase() })}
                  </h2>
                  <h1 className="text-4xl lg:text-5xl xl:text-6xl mb-6 text-gray-800 font-heading-main">
                    {t('about.profileVideo')}
                  </h1>
                  {/* <p className="text-gray-600 leading-relaxed text-base lg:text-lg font-body">
                    {t('about.videoDescription')}
                  </p> */}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* At a Glance Section */}
        <div className="py-16 lg:py-24 bg-gray-50">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            
            {/* Section Header */}
            <div className="mb-12">
              <h2 className="text-4xl lg:text-5xl mb-8 text-gray-600 font-heading-main">
                {t('about.atAGlance')}
              </h2>
              
              {/* Waiting Since */}
              <div className="flex items-center mb-8">
                <div className="w-10 h-10 rounded-full flex items-center justify-center mr-3 bg-accent">
                  <span className="material-icons text-gray-600" style={{ fontSize: '22px' }}>calendar_month</span>
                </div>
                <div>
                  <span className="text-sm font-semibold font-body">
                    {t('about.waitingSince').toUpperCase()}
                  </span>
                  <span className="text-sm font-bold ml-2 font-body">
                    {new Date(family.waiting_for || '2020-03-01').toLocaleDateString('en-US', { 
                      month: 'long', 
                      year: 'numeric' 
                    }).toUpperCase()}
                  </span>
                </div>
              </div>
              
              {/* Green divider line */}
              <div className="w-full h-1 mb-12 bg-dark"></div>
            </div>

            <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">
              
              {/* Family Members - Split if couple */}
              {(() => {
                const names = displayName.split(' and ');
                const humans = family.human || [];
                
                // Helper function to calculate age from birth_date
                const calculateAge = (birthDate) => {
                  if (!birthDate) return null;
                  const birth = new Date(birthDate);
                  const today = new Date();
                  let age = today.getFullYear() - birth.getFullYear();
                  const monthDiff = today.getMonth() - birth.getMonth();
                  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
                    age--;
                  }
                  return age;
                };

                // Helper function to capitalize first letter
                const capitalize = (str) => {
                  if (!str) return '';
                  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
                };

                if (names.length === 2 && humans.length >= 2) {
                  return (
                    <>
                      {/* First Person */}
                      <div>
                        <h3 className="text-xl lg:text-2xl mb-4 text-gray-800 font-heading-main">
                          {humans[0]?.first_name ? `${humans[0].first_name}`.trim() : names[0]}
                        </h3>
                        <div className="space-y-2 text-sm">
                          {humans[0]?.ethnicity && (
                            <div>
                              <span className="font-medium font-body">{t('about.fields.ethnicity')}:</span>
                              <span className="ml-2 font-body">{humans[0].ethnicity}</span>
                            </div>
                          )}
                          {humans[0]?.education && (
                            <div>
                              <span className="font-medium font-body">{t('about.fields.education')}:</span>
                              <span className="ml-2 font-body">{humans[0].education}</span>
                            </div>
                          )}
                          {humans[0]?.gender && (
                            <div>
                              <span className="font-medium font-body">{t('about.fields.gender')}:</span>
                              <span className="ml-2 font-body">{capitalize(humans[0].gender)}</span>
                            </div>
                          )}
                          {humans[0]?.religion && (
                            <div>
                              <span className="font-medium font-body">{t('about.fields.religion')}:</span>
                              <span className="ml-2 font-body">{capitalize(humans[0].religion)}</span>
                            </div>
                          )}
                          {humans[0]?.occupation && (
                            <div>
                              <span className="font-medium font-body">{t('about.fields.occupation')}:</span>
                              <span className="ml-2 font-body">{humans[0].occupation}</span>
                            </div>
                          )}
                          {humans[0]?.birth_date && (
                            <div>
                              <span className="font-medium font-body">{t('about.fields.age')}:</span>
                              <span className="ml-2 font-body">{calculateAge(humans[0].birth_date)}</span>
                            </div>
                          )}
                          <div>
                            <span className="font-medium font-body">{t('about.fields.state')}:</span>
                            <span className="ml-2 font-body">{family.contactInfo?.state?.name || t('about.notAvailable')}</span>
                          </div>
                        </div>
                      </div>

                      {/* Second Person */}
                      <div>
                        <h3 className="text-xl lg:text-2xl mb-4 text-gray-800 font-heading-main">
                          {humans[1]?.first_name ? `${humans[1].first_name}`.trim() : names[1]}
                        </h3>
                        <div className="space-y-2 text-sm">
                          {humans[1]?.ethnicity && (
                            <div>
                              <span className="font-medium font-body">{t('about.fields.ethnicity')}:</span>
                              <span className="ml-2 font-body">{humans[1].ethnicity}</span>
                            </div>
                          )}
                          {humans[1]?.education && (
                            <div>
                              <span className="font-medium font-body">{t('about.fields.education')}:</span>
                              <span className="ml-2 font-body">{humans[1].education}</span>
                            </div>
                          )}
                          {humans[1]?.gender && (
                            <div>
                              <span className="font-medium font-body">{t('about.fields.gender')}:</span>
                              <span className="ml-2 font-body">{capitalize(humans[1].gender)}</span>
                            </div>
                          )}
                          {humans[1]?.religion && (
                            <div>
                              <span className="font-medium font-body">{t('about.fields.religion')}:</span>
                              <span className="ml-2 font-body">{capitalize(humans[1].religion)}</span>
                            </div>
                          )}
                          {humans[1]?.occupation && (
                            <div>
                              <span className="font-medium font-body">{t('about.fields.occupation')}:</span>
                              <span className="ml-2 font-body">{humans[1].occupation}</span>
                            </div>
                          )}
                          {humans[1]?.birth_date && (
                            <div>
                              <span className="font-medium font-body">{t('about.fields.age')}:</span>
                              <span className="ml-2 font-body">{calculateAge(humans[1].birth_date)}</span>
                            </div>
                          )}
                          <div>
                            <span className="font-medium font-body">{t('about.fields.state')}:</span>
                            <span className="ml-2 font-body">{family.contactInfo?.state?.name || t('about.notAvailable')}</span>
                          </div>
                        </div>
                      </div>
                    </>
                  );
                } else {
                  // Single person - spans 2 columns
                  const person = humans[0];
                  return (
                    <div className="lg:col-span-2">
                      <h3 className="text-xl lg:text-2xl mb-4 text-gray-800 font-heading-main">
                        {person?.first_name ? `${person.first_name}`.trim() : displayName}
                      </h3>
                      <div className="grid md:grid-cols-2 gap-8">
                        <div className="space-y-2 text-sm">
                          {person?.ethnicity && (
                            <div>
                              <span className="font-medium font-body">{t('about.fields.ethnicity')}:</span>
                              <span className="ml-2 font-body">{person.ethnicity}</span>
                            </div>
                          )}
                          {person?.education && (
                            <div>
                              <span className="font-medium font-body">{t('about.fields.education')}:</span>
                              <span className="ml-2 font-body">{person.education}</span>
                            </div>
                          )}
                          {person?.gender && (
                            <div>
                              <span className="font-medium font-body">{t('about.fields.gender')}:</span>
                              <span className="ml-2 font-body">{capitalize(person.gender)}</span>
                            </div>
                          )}
                          {person?.religion && (
                            <div>
                              <span className="font-medium font-body">{t('about.fields.religion')}:</span>
                              <span className="ml-2 font-body">{capitalize(person.religion)}</span>
                            </div>
                          )}
                        </div>
                        <div className="space-y-2 text-sm">
                          {person?.occupation && (
                            <div>
                              <span className="font-medium font-body">{t('about.fields.occupation')}:</span>
                              <span className="ml-2 font-body">{person.occupation}</span>
                            </div>
                          )}
                          {person?.birth_date && (
                            <div>
                              <span className="font-medium font-body">{t('about.fields.age')}:</span>
                              <span className="ml-2 font-body">{calculateAge(person.birth_date)}</span>
                            </div>
                          )}
                          <div>
                            <span className="font-medium font-body">{t('about.fields.state')}:</span>
                            <span className="ml-2 font-body">{family.contactInfo?.state?.name || t('about.notAvailable')}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }
              })()}

              {/* Our Adoption Preferences */}
              <div>
                <h3 className="text-xl lg:text-2xl mb-6 text-gray-800 font-heading-main">
                  {t('about.adoptionPreferences', { familyTypeText2 })}
                </h3>
                
                <div className="space-y-3 text-sm">
                  {/* Gender Preference */}
                  {family.preference?.gender?.length > 0 && (
                    <div>
                      <span className="font-semibold font-body">{t('about.preferences.gender')}:</span>
                      <span className="ml-2 font-body">
                        { family.preference.gender.join(', ') }
                        {/* { family.preference.gender } */}
                      </span>
                    </div>
                  )}
                  
                  {/* Number of Children */}
                  {family.preference?.number_of_children?.length > 0 && (
                    <div>
                      <span className="font-semibold font-body">{t('about.preferences.numberOfChildren')}:</span>
                      <span className="ml-2 font-body">
                        {family.preference.number_of_children.join(', ')}
                      </span>
                    </div>
                  )}
                  
                  {/* Age Range */}
                  {family.preference?.age?.length > 0 && (
                    <div>
                      <span className="font-semibold font-body">{t('about.preferences.age')}:</span>
                      <span className="ml-2 font-body">
                        {family.preference.age.join(', ')}
                      </span>
                    </div>
                  )}
                  
                  {/* Ethnicity Preferences */}
                  {family.preference?.ethnicity?.length > 0 && (
                    <div>
                      <span className="font-semibold block mb-1 font-body">{t('about.preferences.ethnicity')}:</span>
                      <p className="text-gray-600 leading-relaxed font-body">
                        {family.preference.ethnicity.join(', ')}
                      </p>
                    </div>
                  )}
                  
                  {/* Adoption Type */}
                  {family.preference?.adoption_type?.length > 0 && (
                    <div>
                      <span className="font-semibold font-body">{t('about.preferences.adoptionType')}:</span>
                      <span className="ml-2 font-body">
                        {family.preference.adoption_type.join(', ')}
                      </span>
                    </div>
                  )}
                  
                  {/* Birth Father Status */}
                  {family.preference?.birth_father_status?.length > 0 && (
                    <div>
                      <span className="font-semibold font-body">{t('about.preferences.birthFatherStatus')}:</span>
                      <span className="ml-2 font-body">
                        {family.preference.birth_father_status.join(', ')}
                      </span>
                    </div>
                  )}
                  
                  {/* Special Needs */}
                  {family.preference?.ready_to_adopt_special_need_child !== undefined && (
                    <div>
                      <span className="font-semibold font-body">{t('about.preferences.openToSpecialNeeds')}:</span>
                      <span className="ml-2 font-body">
                        {family.preference.ready_to_adopt_special_need_child ? t('common.yes') : t('common.no')}
                      </span>
                    </div>
                  )}
                  
                  {/* Special Needs Details */}
                  {family.preference?.special_needs?.length > 0 && (
                    <div>
                      <span className="font-semibold block mb-1 font-body">{t('about.preferences.specialNeedsConsidered')}:</span>
                      <p className="text-gray-600 leading-relaxed font-body">
                        {family.preference.special_needs.join(', ')}
                      </p>
                    </div>
                  )}
                  
                  {/* Fallback message if no preferences are set */}
                  {!family.preference && (
                    <div className="text-gray-500 italic font-body">
                      {t('about.preferences.finalizingMessage')}
                    </div>
                  )}
                </div>

                {/* Contact Agency Button */}
                <div className="mt-8">
                  <div className="flex items-center mb-3">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center mr-3 bg-accent">
                      <span className="material-icons text-gray-600" style={{ fontSize: '20px' }}>call</span>
                    </div>
                    <span className="font-semibold text-xs font-body">
                      {t('about.contactAgency', { familyTypeText2: familyTypeText2.toUpperCase() })}
                    </span>
                  </div>
                  <a 
                    href={`tel:${callNumber}`}
                    className="text-base font-bold block font-body" 
                  >
                    {formattedPhone(callNumber)}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Send Us a Message Section */}
        <ContactForm 
          accountId={family.mongoId}
          agencyId={family.agency.mongoId}
          familyName={familyName}
          familyType={family.account_type}
        />

      </main>

      <AdoptionFooter family={family} />
    </div>
  )
}

export const Head = ({ data }) => (
  <SEO title={`About ${data.account?.name || 'Our Family'} - Adoption Journey`} />
)

export const query = graphql`
  query($accountId: String!, $language: String!) {
    account(id: { eq: $accountId }) {
      id
      mongoId
      name
      status
      account_type
      waiting_for
      contactInfo {
        website
        state {
          name
        }
      }
      human {
        first_name
        gender
        birth_date
        occupation
        religion
        ethnicity
        education
      }
      avatar
      banner_photo
      default_letter {
        title
        description
        translations {
          lan
          title
          description
        }
      }
      default_video
      videos {
        _id
        name
        url
        type
      }
      flipbook {
        pdf_template
        agency
        flipbook
        type
        heyzine_data {
          id
          url
          thumbnail
          pdf
        }
        status
      }
      preference {
        gender
        number_of_children
        age
        ethnicity
        adoption_type
        birth_father_status
        ready_to_adopt_special_need_child
        special_needs
      }
      agency {
        mongoId
        name
        address {
          line_1
          line_2
          street
          city
          state {
            name
          }
          zip
        }
        email {
          email
        }
        phone {
          number
        }
        bm_contact {
          email
          phone
          text
        }
        website
        avatar
      }
    }
    locales: allLocale(filter: {language: {eq: $language}}) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`

export default FamilyAbout